import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import bgDesktop from "../images/page-1-2.jpg";
import bgMobile from "../images/page-1-mobile.jpg";

// ======= DESKTOP Konstanten / Funktionen =======
const ORIGINAL_W = 2048;
const ORIGINAL_H = 1151;

const emailRectPx = [
    (0.642422 - 0.04) * ORIGINAL_W,
    0.781434 * ORIGINAL_H,
    (0.642422 - 0.04 + 0.106016) * ORIGINAL_W,
    (0.781434 + 0.03) * ORIGINAL_H,
];
const wwwRectPx = [
    (0.758203 - 0.05) * ORIGINAL_W,
    0.781434 * ORIGINAL_H,
    (0.758203 - 0.05 + 0.1178906) * ORIGINAL_W,
    (0.781434 + 0.03) * ORIGINAL_H,
];

function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / ORIGINAL_W, containerH / ORIGINAL_H);
    const renderedW = ORIGINAL_W * scale;
    const renderedH = ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

// ⭐ NEU: Mobile Originalgrößen
//const MOB_W = 1536;
const MOB_H = 864;

// ⭐ NEU: Mail-Hotspot in Original-PX-Koordinaten (x1,y1,x2,y2)
const emailRectMobPx = [900, 690, 1170, 740];

// ⭐ NEU: Hilfsfunktion: Pixel -> gerenderte Pixel
function rectPxToRenderedPx([x1, y1, x2, y2], scale) {
    const left = x1 * scale;
    const top = y1 * scale;
    const width = (x2 - x1) * scale;
    const height = (y2 - y1) * scale;
    return { left, top, width, height };
}

// ======= PAGE COMPONENT =======
const Page1 = () => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

    // ---------- DESKTOP ----------
    const wrapperRef = useRef(null);
    const [emailBox, setEmailBox] = useState({});
    const [wwwBox, setWwwBox] = useState({});

    useLayoutEffect(() => {
        if (isMobile) return; // Desktop only
        const recompute = () => {
            const el = wrapperRef.current;
            if (!el) return;
            const w = el.clientWidth;
            const h = el.clientHeight;
            setEmailBox(rectForCoverToPercent(emailRectPx, w, h));
            setWwwBox(rectForCoverToPercent(wwwRectPx, w, h));
        };
        recompute();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(recompute);
            ro.observe(wrapperRef.current);
            return () => ro.disconnect();
        } else {
            window.addEventListener("resize", recompute);
            return () => window.removeEventListener("resize", recompute);
        }
    }, [isMobile]);

    // ---------- MOBILE ----------
    const scrollRef = useRef(null);
    const imgRef = useRef(null);                  // ⭐ NEU
    const [emailBoxMob, setEmailBoxMob] = useState(null); // ⭐ NEU

    useEffect(() => {
        if (!isMobile) return;
        const sc = scrollRef.current;
        const img = imgRef.current;
        if (!sc || !img) return;

        const compute = () => {
            // ⭐ ADJUST: statt containerH nutzen wir die *tatsächlich gerenderte Bildgröße*
            const imgRect = img.getBoundingClientRect();
            const scale = imgRect.height / MOB_H;        // präziser Höhen-Scale
            const renderedW = imgRect.width;             // tatsächliche Bildbreite in CSS px
            // (Top/Left Offsets des Bildes sind 0, da es im Scroll-Container ohne Margin liegt)

            // Mail-Box in gerenderte PX umrechnen
            const b = rectPxToRenderedPx(emailRectMobPx, scale);
             
            const MOBILE_OFFSETS = {
                x: -100.5,  // Links/Rechts
                y: -10,     // Oben/Unten
                w: -40,     // Breite
                h: -20      // Höhe
            };
            
            setEmailBoxMob({
                left: b.left + MOBILE_OFFSETS.x + "px",
                top: b.top + MOBILE_OFFSETS.y + "px",
                width: b.width + MOBILE_OFFSETS.w + "px",
                height: b.height + MOBILE_OFFSETS.h + "px",
                position: "absolute",
                display: "block",
               //  background: "#d6000059", deine Testfarbe
            });

            // nach rechts scrollen (anhand der *Bild*-Breite)
            sc.scrollLeft = Math.max(0, renderedW - sc.clientWidth);
        };

        if (img.complete) compute();
        else img.addEventListener("load", compute, { once: true });

        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(compute);
            ro.observe(sc);
        } else {
            window.addEventListener("resize", compute);
        }
        return () => {
            if (img) img.removeEventListener?.("load", compute);
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", compute);
        };
    }, [isMobile]);
    // ---------- RENDER ----------
    if (isMobile) {
        // ===== MOBILE VIEW =====
        return (
            <div
                ref={scrollRef}
                style={{
                    width: "100vw",
                    height: "100vh",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x",
                    position: "relative",
                }}
            >
                <img
                    ref={imgRef} // ⭐ NEU
                    src={bgMobile}
                    alt=""
                    style={{ height: "100%", width: "auto", display: "block" }}
                />

                {/* ⭐ NEU: Mail-Hotspot relativ zum gerenderten Bild */}
                {emailBoxMob && (
                    <a
                        href="mailto:mail@tw-a.net"
                        style={emailBoxMob}
                        title="E-Mail an mail@tw-a.net"
                    />
                )}
            </div>
        );
    }

    // ===== DESKTOP VIEW (unverändert) =====
    return (
        <div
            ref={wrapperRef}
            style={{
                position: "relative",
                width: "100vw",
                height: "100vh",
                overflow: "hidden",
                cursor: "pointer",
            }}
        >
            <img
                src={bgDesktop}
                alt=""
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    pointerEvents: "none",
                }}
            />

            <a
                href="mailto:mail@tw-a.net"
                style={{
                    position: "absolute",
                    ...emailBox,
                    color: "transparent",
                    display: "block",
                   
                }}
                target="_blank"
                rel="noopener noreferrer"
                title="E-Mail an mail@tw-a.net"
            >
                Mail
            </a>

            <a
                href="https://tw-a.net"
                style={{
                    position: "absolute",
                    ...wwwBox,
                    color: "transparent",
                    display: "block",
                }}
                target="_blank"
                rel="noopener noreferrer"
                title="www.tw-a.net besuchen"
            >
                Website
            </a>
        </div>
    );
};

export default Page1;
